import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Upload, Trash2, Check, Loader2, ImageIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';

interface ArcheSummary {
  archId: string;
  name: string;
  simpleDescription: string;
  imageRef: string | null;
}

type UploadStatus = 'idle' | 'getting-url' | 'uploading' | 'confirming' | 'done' | 'error';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

export function ArcheIconManager() {
  const [arches, setArches] = useState<ArcheSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedArchId, setSelectedArchId] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchArches = useCallback(async () => {
    try {
      setLoading(true);
      const result = await api.listArches({ limit: 200 });
      const mapped = (result.arches as ArcheSummary[]).map((a) => ({
        archId: a.archId,
        name: a.name,
        simpleDescription: a.simpleDescription,
        imageRef: a.imageRef,
      }));
      mapped.sort((a, b) => a.name.localeCompare(b.name));
      setArches(mapped);
    } catch (err) {
      console.error('Failed to fetch arches', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArches();
  }, [fetchArches]);

  const selectedArche = arches.find((a) => a.archId === selectedArchId) ?? null;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedArchId) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadStatus('error');
      setStatusMessage(`Invalid file type. Allowed: PNG, JPEG, WebP, SVG`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadStatus('error');
      setStatusMessage('File too large. Maximum size: 5MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result as string);
    reader.readAsDataURL(file);

    try {
      // Step 1: Get presigned URL
      setUploadStatus('getting-url');
      setStatusMessage('Getting upload URL...');
      const { uploadUrl, s3Key } = await api.adminGetArcheIconUploadUrl(
        selectedArchId,
        file.type,
        file.size,
      );

      // Step 2: Upload to S3
      setUploadStatus('uploading');
      setStatusMessage('Uploading to S3...');
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status}`);
      }

      // Step 3: Confirm upload
      setUploadStatus('confirming');
      setStatusMessage('Confirming upload...');
      const { arche } = await api.adminConfirmArcheIcon(selectedArchId, s3Key);

      // Update local state
      setArches((prev) =>
        prev.map((a) => (a.archId === selectedArchId ? { ...a, imageRef: arche.imageRef } : a)),
      );

      setUploadStatus('done');
      setStatusMessage(`Icon updated for ${arche.name}`);
    } catch (err) {
      setUploadStatus('error');
      setStatusMessage(err instanceof Error ? err.message : 'Upload failed');
      console.error('Icon upload failed', err);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!selectedArchId) return;

    try {
      setUploadStatus('confirming');
      setStatusMessage('Removing icon...');
      await api.adminDeleteArcheIcon(selectedArchId);

      setArches((prev) =>
        prev.map((a) => (a.archId === selectedArchId ? { ...a, imageRef: null } : a)),
      );
      setPreview(null);
      setUploadStatus('done');
      setStatusMessage('Icon removed');
    } catch (err) {
      setUploadStatus('error');
      setStatusMessage(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const resetUpload = () => {
    setUploadStatus('idle');
    setStatusMessage('');
    setPreview(null);
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="mb-6">
        <Link
          to="/dev-console"
          className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dev Console
        </Link>
        <h1 className="text-3xl font-bold text-foreground">Arche Icon Manager</h1>
        <p className="mt-2 text-muted-foreground">
          Upload and manage icons for arches. Icons are served via CloudFront CDN.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Arche selector */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Select an Arche</CardTitle>
            <CardDescription>Choose which arche to update the icon for</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-1">
                {arches.map((arche) => (
                  <button
                    key={arche.archId}
                    onClick={() => {
                      setSelectedArchId(arche.archId);
                      resetUpload();
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      selectedArchId === arche.archId
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-md flex-shrink-0 overflow-hidden">
                      {arche.imageRef ? (
                        <img src={arche.imageRef} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <ImageIcon className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{arche.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{arche.archId}</div>
                    </div>
                    {arche.imageRef && (
                      <Check className="h-4 w-4 text-green-500 ml-auto flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {selectedArche ? `Icon: ${selectedArche.name}` : 'Upload Icon'}
            </CardTitle>
            <CardDescription>
              {selectedArche
                ? 'Upload a new icon image (PNG, JPEG, WebP, or SVG, max 5MB)'
                : 'Select an arche from the list first'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!selectedArche ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <ImageIcon className="h-12 w-12" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Current icon preview */}
                <div>
                  <Label className="text-sm text-muted-foreground">Current Icon</Label>
                  <div className="mt-2 flex items-center gap-4">
                    <div className="w-20 h-20 rounded-xl overflow-hidden border border-border">
                      {preview || selectedArche.imageRef ? (
                        <img
                          src={preview || selectedArche.imageRef || ''}
                          alt={selectedArche.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-b from-primary/80 to-primary flex items-center justify-center">
                          <span className="text-2xl font-bold text-primary-foreground">
                            {selectedArche.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    {selectedArche.imageRef && (
                      <div className="text-xs text-muted-foreground break-all max-w-48">
                        {selectedArche.imageRef}
                      </div>
                    )}
                  </div>
                </div>

                {/* Upload controls */}
                <div className="space-y-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALLOWED_TYPES.join(',')}
                    onChange={handleFileSelect}
                    className="hidden"
                    id="icon-upload"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={
                        uploadStatus === 'getting-url' ||
                        uploadStatus === 'uploading' ||
                        uploadStatus === 'confirming'
                      }
                      className="flex-1"
                    >
                      {uploadStatus === 'getting-url' ||
                      uploadStatus === 'uploading' ||
                      uploadStatus === 'confirming' ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="h-4 w-4 mr-2" />
                      )}
                      Upload Icon
                    </Button>
                    {selectedArche.imageRef && (
                      <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={
                          uploadStatus === 'getting-url' ||
                          uploadStatus === 'uploading' ||
                          uploadStatus === 'confirming'
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Status message */}
                {statusMessage && (
                  <div
                    className={`text-sm px-3 py-2 rounded-md ${
                      uploadStatus === 'error'
                        ? 'bg-destructive/10 text-destructive'
                        : uploadStatus === 'done'
                          ? 'bg-green-500/10 text-green-600 dark:text-green-400'
                          : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {statusMessage}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ArcheIconManager;
